/**
 * ---------------------------------------------------------
 * EntitySearchSelect
 * ---------------------------------------------------------
 *
 * Generic reusable dropdown with:
 * - search input
 * - debounced query
 * - infinite scroll (TanStack Query)
 * - selected entity sync via detail hook
 *
 * Designed for entity-based selection (customer, project, user, service, etc).
 *
 * 📌 Architecture:
 * - useInfiniteHook → must return Paginated<T> with { items }
 * - useDetailHook → must return single entity by id
 * - getLabel → defines how entity is shown inside input
 *
 * 📌 When to use:
 * - Selecting one entity from server-side paginated list
 * - Need search + infinite scroll
 * - Need selected id synchronization
 *
 * 📌 Not for:
 * - static options
 * - multi-select
 * - small local arrays
 *
 * ---------------------------------------------------------
 *
 * Example (Customer):
 *
 * <EntitySearchSelect
 *   value={customerId}
 *   onChange={(id) => setCustomerId(id)}
 *   useInfiniteHook={useInfiniteCustomers}
 *   useDetailHook={useCustomerDetail}
 *   searchParamName="name"
 *   getLabel={(c) => c.name}
 * />
 *
 * ---------------------------------------------------------
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styles from './EntitySearchSelect.module.css'
import { useDebounce } from '../../../hooks/useDebounce'
import { Input } from '../../controls/Input/Input'

interface BaseEntity {
  id: number
}

interface Props<T extends BaseEntity> {

  /** Currently selected entity id */
  value: number | null | undefined

  /** Callback when entity selected */
  onChange: (id: number | null, item?: T) => void


  /** Infinite query hook returning Paginated<T> */
  useInfiniteHook: (
    params: any,
    options?: { enabled?: boolean }
  ) => any


  /** Detail query hook returning T by id */
  useDetailHook: (id: number | undefined,
    options?: { enabled?: boolean }
  ) => any
  /** Backend search parameter name (e.g. "name", "title") */
  searchParamName: string

  /** Page size for infinite loading */
  limit?: number

  /** Function defining label rendering inside input */
  getLabel: (item: T) => string

  /** Custom render for dropdown item */
  renderItem?: (item: T) => React.ReactNode

  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  className?: string
  hasError?: boolean

  extraParams?: Record<string, unknown>
  extraValues?: T[]

  onInputChange?: (value: string) => void
  inputValue?: string

}


export function EntitySearchSelect<T extends BaseEntity>({ // TODO3 documentation 
  value,
  onChange,
  useInfiniteHook,
  useDetailHook,
  searchParamName,
  limit = 20,
  getLabel,
  renderItem,
  placeholder = 'Suchen…',
  disabled,
  readOnly,
  className,
  extraParams,
  hasError,
  extraValues,
  onInputChange,
  inputValue
}: Props<T>) {
  const [internalQuery, setInternalQuery] = useState('')
  const query = inputValue !== undefined
    ? inputValue
    : internalQuery

  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounce(query, 400)
  const filters = useMemo(() => {
    const base = {
      limit,
      ...extraParams,
    }

    if (debouncedQuery?.trim()) {
      return {
        ...base,
        [searchParamName]: debouncedQuery.trim(),
      }
    }

    return base
  }, [limit, searchParamName, debouncedQuery, extraParams])




  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true)
    }
  }, [isOpen])

  // 🔹 detail only if value exists
  const { data: selectedItem } = useDetailHook(value ?? undefined, {
    enabled: !!value,
  })

  // 🔹 list only after first open
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteHook(filters, {
    enabled: isOpen,
  })

  const apiItems: T[] = data?.items ?? []

  const items: T[] = useMemo(() => {
    if (!extraValues?.length) return apiItems

    return [...extraValues, ...apiItems]
  }, [apiItems, extraValues])






  // sync selected label
  useEffect(() => {
    if (selectedItem && !query) {
      setInternalQuery(getLabel(selectedItem))
    }
  }, [selectedItem])

  // outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleScroll = () => {
    const el = listRef.current
    if (!el || !hasNextPage || isFetchingNextPage) return

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      fetchNextPage()
    }
  }

  const handleSelect = (item: T) => {
    onChange(item.id, item)

    const label = getLabel(item)

    if (inputValue === undefined) {
      setInternalQuery(label)
    }

    onInputChange?.(label)   // 🔥 ОСЬ ЦЕ ГОЛОВНЕ

    setIsOpen(false)
  }



  const isEmptySearch =
    !isFetching &&
    items.length === 0 &&
    debouncedQuery?.trim().length > 0

  return (
    <div ref={containerRef} className={styles.main}>
      <Input
        value={query}
        onChange={(value) => {
          if (readOnly) return
          setInternalQuery(value)
          onInputChange?.(value)
          if (value.trim() === '') {
            onChange(null)
          }
          if (!isOpen) setIsOpen(true)
        }}
        onFocus={() => { if (!readOnly) setIsOpen(true) }}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        type="search"
        className='w-full'
      />

      {isOpen && (
        <div
          ref={listRef}
          className={styles.list}
          onScroll={handleScroll}
        >
          {items.map((item) => (
            <div
              key={item.id}
              className={styles.item}
              onClick={() => handleSelect(item)}
            >
              {renderItem
                ? renderItem(item)
                : getLabel(item)}
            </div>
          ))}

          {isFetchingNextPage && (
            <div className={styles.muted}>Loading…</div>
          )}

          {isEmptySearch && (
            <div className={styles.muted}>
              Keine Ergebnisse
            </div>
          )}
        </div>
      )}
    </div>
  )
}
